/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { HTMLAttributes, useEffect, useState, createPortal } from 'preact/compat';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import {
  Price,
  Button,
  IllustratedMessage,
  Picker,
  Pagination,
  InLineAlert,
  Icon,
  type PickerOption,
} from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import {
  QuoteTemplatesListTable as QuoteTemplatesListTableComponent,
  QuoteTemplateRowData,
  ConfirmationModal,
} from '@/quote-management/components';
import { getQuoteTemplates, generateQuoteFromTemplate } from '@/quote-management/api';
import { getDefaultPageSizeOptions } from '@/quote-management/data/transforms';
import {
  NegotiableQuoteTemplatesListModel,
  NegotiableQuoteTemplateListEntry,
} from '@/quote-management/data/models';
import { formattedDate } from '@/quote-management/utils/dateUtils';

export interface QuoteTemplatesListTableProps
  extends HTMLAttributes<HTMLDivElement> {
  pageSize?: number;
  showItemRange?: boolean;
  showPageSizePicker?: boolean;
  showPagination?: boolean;
  onViewQuoteTemplate?: (templateId: string, templateName: string, status: string) => void;
  onGenerateQuoteFromTemplate?: (templateId: string, templateName: string, quoteId: string) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  slots?: {
    /** Slot for customizing the template name cell content */
    Name?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the state cell content */
    State?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the status cell content */
    Status?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the valid until cell content */
    ValidUntil?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the min quote total cell content */
    MinQuoteTotal?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the orders placed cell content */
    OrdersPlaced?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the last ordered cell content */
    LastOrdered?: SlotProps<{ template: NegotiableQuoteTemplateListEntry }>;
    /** Slot for customizing the actions cell content */
    Actions?: SlotProps<{
      template: NegotiableQuoteTemplateListEntry;
      onViewQuoteTemplate?: (id: string, name: string, status: string) => void;
      onGenerateQuoteFromTemplate?: (id: string, name: string) => void;
    }>;
    /** Slot for customizing the empty templates message */
    EmptyTemplates?: SlotProps;
    /** Slot for customizing the item range display */
    ItemRange?: SlotProps<{
      startItem: number;
      endItem: number;
      totalCount: number;
      currentPage: number;
      pageSize: number;
    }>;
    /** Slot for customizing the page size picker */
    PageSizePicker?: SlotProps<{
      pageSize: number;
      pageSizeOptions: number[];
      onPageSizeChange?: (pageSize: number) => void;
    }>;
    /** Slot for customizing the pagination */
    Pagination?: SlotProps<{
      currentPage: number;
      totalPages: number;
      onChange?: (page: number) => void;
    }>;
  };
}

export const QuoteTemplatesListTable: Container<
  QuoteTemplatesListTableProps
> = ({
  pageSize,
  showItemRange = true,
  showPageSizePicker = true,
  showPagination = true,
  onViewQuoteTemplate,
  onGenerateQuoteFromTemplate,
  onPageSizeChange,
  onPageChange,
  slots,
  ...props
}) => {
  const dictionary = useText({
    view: 'QuoteManagement.QuoteTemplatesListTable.view',
    generateQuote: 'NegotiableQuoteTemplate.Manage.actionButtons.generateQuote',
    generateQuoteModalMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.message',
    generateQuoteModalCancel: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancel',
    generateQuoteModalConfirm: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.confirm',
    generateQuoteModalConfirmLoading: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.confirmLoading',
    generateQuoteModalSuccessHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.successHeading',
    generateQuoteSuccessMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.successDescription',
    generateQuoteModalErrorHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.errorHeading',
    generateQuoteErrorFallback: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.errorFallback',
  });

  const [templatesData, setTemplatesData] =
    useState<NegotiableQuoteTemplatesListModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGenerateQuoteModal, setShowGenerateQuoteModal] = useState<boolean>(false);
  const [isGeneratingQuote, setIsGeneratingQuote] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<{ id: string; name: string } | null>(null);
  const [generateQuoteSuccessMessage, setGenerateQuoteSuccessMessage] = useState<string>('');
  const [generateQuoteErrorMessage, setGenerateQuoteErrorMessage] = useState<string>('');

  // Default to first page size option if no pageSize prop provided
  const defaultPageSize = getDefaultPageSizeOptions()[0];
  const [currentPageSize, setCurrentPageSize] = useState(
    pageSize || defaultPageSize
  );
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch quote templates data when authenticated
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const data = await getQuoteTemplates({
          pageSize: currentPageSize,
          currentPage,
        });
        setTemplatesData(data);
      } catch (error) {
        console.error('Failed to fetch quote templates:', error);
      } finally {
        setLoading(false);
      }
    };

    // Listen for authentication events
    const unsubscribe = events.on(
      'authenticated',
      (isAuthenticated) => {
        if (isAuthenticated) {
          fetchTemplates();
        } else {
          setTemplatesData(null);
          setLoading(false);
        }
      },
      { eager: true }
    ); // eager: true means it fires immediately with current state

    return () => {
      unsubscribe?.off();
    };
  }, [currentPageSize, currentPage]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 when page size changes
    onPageSizeChange?.(newPageSize); // Also call parent callback
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    onPageChange?.(newPage); // Also call parent callback
  };

  // Handle page size picker selection
  const handlePageSizeSelect = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const value = target?.value;
    if (value) {
      handlePageSizeChange(Number(value));
    }
  };

  // Handle generate quote button click
  const handleGenerateQuoteClick = (templateId: string, templateName: string) => {
    setSelectedTemplate({ id: templateId, name: templateName });
    setGenerateQuoteSuccessMessage('');
    setGenerateQuoteErrorMessage('');
    setShowGenerateQuoteModal(true);
  };

  // Handle generate quote confirmation
  const handleGenerateQuote = async (template: { id: string; name: string }) => {    
    setIsGeneratingQuote(true);
    setGenerateQuoteSuccessMessage('');
    setGenerateQuoteErrorMessage('');
    
    try {
      const result = await generateQuoteFromTemplate({ templateId: template.id });
      
      // Show success message
      setGenerateQuoteSuccessMessage(dictionary.generateQuoteSuccessMessage);
      
      // Call the callback AFTER successful generation
      onGenerateQuoteFromTemplate?.(template.id, template.name, result.quoteId);
    } catch (error) {
      console.error('Failed to generate quote from template:', error);
      // Show error message and keep modal open so user can try again or cancel
      const errorMessage = error instanceof Error ? error.message : dictionary.generateQuoteErrorFallback;
      setGenerateQuoteErrorMessage(errorMessage);
    } finally {
      setIsGeneratingQuote(false);
    }
  };

  // Handle generate quote modal cancel
  const handleGenerateQuoteModalCancel = () => {
    setShowGenerateQuoteModal(false);
    setSelectedTemplate(null);
    setGenerateQuoteSuccessMessage('');
    setGenerateQuoteErrorMessage('');
  };

  // Prepare transformed quote template data for table component
  const prepareRowData = (
    templates: NegotiableQuoteTemplateListEntry[],
    slots?: QuoteTemplatesListTableProps['slots'],
    onViewQuoteTemplate?: (templateId: string, templateName: string, status: string) => void
  ): QuoteTemplateRowData[] => {
    return templates
      .filter((template) => template?.uid) // Filter out null templates
      .map((template) => {
        return {
          id: template.id,
          name: (
            <Slot name="Name" slot={slots?.Name} context={{ template }}>
              <span>{template.name}</span>
            </Slot>
          ),
          state: (
            <Slot name="State" slot={slots?.State} context={{ template }}>
              <span style={{ textTransform: 'capitalize' }}>
                {template.state?.replace(/_/g, ' ') || '-'}
              </span>
            </Slot>
          ),
          status: (
            <Slot name="Status" slot={slots?.Status} context={{ template }}>
              <span>{template.status}</span>
            </Slot>
          ),
          validUntil: (
            <Slot
              name="ValidUntil"
              slot={slots?.ValidUntil}
              context={{ template }}
            >
              <span>{formattedDate(template.expirationDate, 'short')}</span>
            </Slot>
          ),
          minQuoteTotal: (
            <Slot
              name="MinQuoteTotal"
              slot={slots?.MinQuoteTotal}
              context={{ template }}
            >
              <Price
                amount={template.prices.minNegotiatedGrandTotal.value}
                currency={template.prices.minNegotiatedGrandTotal.currency}
              />
            </Slot>
          ),
          ordersPlaced: (
            <Slot
              name="OrdersPlaced"
              slot={slots?.OrdersPlaced}
              context={{ template }}
            >
              <span>{template.ordersPlaced}</span>
            </Slot>
          ),
          lastOrdered: (
            <Slot
              name="LastOrdered"
              slot={slots?.LastOrdered}
              context={{ template }}
            >
              <span>{formattedDate(template.lastOrderedAt, 'short')}</span>
            </Slot>
          ),
          actions: (
            <Slot
              name="Actions"
              slot={slots?.Actions}
              context={{ template, onViewQuoteTemplate }}
              className="quote-management-quote-templates-list-table__actions-cell"
            >
              <Button
                variant="tertiary"
                size="medium"
                onClick={() => onViewQuoteTemplate?.(template.id, template.name, template.status)}
              >
                {dictionary.view}
              </Button>
              {template.canGenerateQuoteFromTemplate && (
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => handleGenerateQuoteClick(template.id, template.name)}
                  data-testid={`generate-quote-button-${template.id}`}
                >
                  {dictionary.generateQuote}
                </Button>
              )}
            </Slot>
          ),
        };
      });
  };

  // Prepare data for rendering
  const rowData = templatesData?.items
    ? prepareRowData(templatesData.items, slots, onViewQuoteTemplate)
    : [];

  // Use pagination info directly from API response, merging in pageSizeOptions if needed
  const paginationInfo = templatesData?.paginationInfo
    ? {
        ...templatesData.paginationInfo,
        pageSizeOptions: getDefaultPageSizeOptions(),
      }
    : undefined;

  const shouldShowPagination = !!paginationInfo;

  // Empty state message
  const emptyStateMessage = (
    <Slot
      name="EmptyTemplates"
      slot={slots?.EmptyTemplates}
      context={{ templatesData }}
    >
      <IllustratedMessage heading="No Quote Templates Found" />
    </Slot>
  );

  // Item range message
  const itemRangeMessage = paginationInfo ? (
    <Slot name="ItemRange" slot={slots?.ItemRange} context={paginationInfo}>
      <span>
        Items {paginationInfo.startItem} to {paginationInfo.endItem} of{' '}
        {paginationInfo.totalCount} total
      </span>
    </Slot>
  ) : undefined;

  // Prepare page size picker message for component
  const pageSizePickerMessage =
    paginationInfo && paginationInfo.pageSizeOptions ? (
      <Slot
        name="PageSizePicker"
        slot={slots?.PageSizePicker}
        context={{
          pageSize: paginationInfo.pageSize,
          pageSizeOptions: paginationInfo.pageSizeOptions,
          onPageSizeChange: handlePageSizeChange,
        }}
      >
        <span>Show </span>
        <Picker
          variant="primary"
          size="medium"
          value={String(paginationInfo.pageSize)}
          options={paginationInfo.pageSizeOptions.map(
            (size): PickerOption => ({
              value: String(size),
              text: String(size),
            })
          )}
          handleSelect={handlePageSizeSelect}
        />
        <span> per page</span>
      </Slot>
    ) : undefined;

  // Prepare pagination message for component
  const paginationMessage = paginationInfo ? (
    <Slot
      name="Pagination"
      slot={slots?.Pagination}
      context={{
        currentPage: paginationInfo.currentPage,
        totalPages: paginationInfo.totalPages,
        onChange: handlePageChange,
      }}
    >
      <Pagination
        currentPage={paginationInfo.currentPage}
        totalPages={paginationInfo.totalPages}
        onChange={handlePageChange}
      />
    </Slot>
  ) : undefined;

  // Create banner for generate quote modal based on success/error state
  let generateQuoteModalBanner = null;
  if (generateQuoteSuccessMessage) {
    generateQuoteModalBanner = (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<Icon source="CheckWithCircle" />}
        heading={dictionary.generateQuoteModalSuccessHeading}
        description={generateQuoteSuccessMessage}
        data-testid="generate-quote-success-banner"
      />
    );
  } else if (generateQuoteErrorMessage) {
    generateQuoteModalBanner = (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<Icon source="WarningFilled" />}
        heading={dictionary.generateQuoteModalErrorHeading}
        description={generateQuoteErrorMessage}
        data-testid="generate-quote-error-banner"
      />
    );
  }

  return (
    <>
      <QuoteTemplatesListTableComponent
        rowData={rowData}
        loading={loading}
        className={props.className as string}
        emptyStateMessage={emptyStateMessage}
        showItemRange={showItemRange && shouldShowPagination}
        itemRangeMessage={itemRangeMessage}
        showPageSizePicker={showPageSizePicker && shouldShowPagination}
        pageSizePickerMessage={pageSizePickerMessage}
        showPagination={showPagination && shouldShowPagination}
        paginationMessage={paginationMessage}
      />
      {showGenerateQuoteModal &&
        createPortal(
          <ConfirmationModal
            open={showGenerateQuoteModal}
            title={dictionary.generateQuote}
            message={dictionary.generateQuoteModalMessage}
            cancelLabel={dictionary.generateQuoteModalCancel}
            confirmLabel={
              isGeneratingQuote
                ? dictionary.generateQuoteModalConfirmLoading
                : dictionary.generateQuoteModalConfirm
            }
            onCancel={handleGenerateQuoteModalCancel}
            onConfirm={isGeneratingQuote || !selectedTemplate ? undefined : () => handleGenerateQuote(selectedTemplate)}
            showCloseButton={!generateQuoteModalBanner}
            confirmationBanner={generateQuoteModalBanner}
            data-testid="generate-quote-confirmation-modal"
          />,
          document.body
        )}
    </>
  );
};
